import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { entities, uploadFile, getDisplayMessage } from "@/api/apiClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

export default function CriarAditivoPage() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const contratoId = urlParams.get("contrato_id");

  const [formData, setFormData] = useState({
    contrato_id: contratoId || "",
    numero_aditivo: "",
    tipo_aditivo: "Prazo",
    data_aditivo: "",
    objeto: "",
    valor_aditado: 0,
    prazo_aditado_dias: 0,
    nova_data_fim: "",
    status: "Rascunho",
    observacoes: "",
  });

  const [arquivo, setArquivo] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { data: contratos = [] } = useQuery({
    queryKey: ["contratos"],
    queryFn: () => entities.Contrato.list(),
  });

  const { data: contrato } = useQuery({
    queryKey: ["contrato", contratoId],
    queryFn: () => entities.Contrato.list().then(contratos => contratos.find(c => c.id === contratoId)),
    enabled: !!contratoId,
  });

  const criarAditivoMutation = useMutation({
    mutationFn: async (dados) => {
      const aditivo = await entities.Aditivo.create(dados);
      
      // Se o aditivo estiver ativo, atualizar o contrato
      if (dados.status === "Ativo") {
        const contratoAtualizado = {};
        
        // Atualizar data de fim se houver nova data ou prazo aditado
        if (dados.nova_data_fim) {
          contratoAtualizado.data_fim = dados.nova_data_fim;
        } else if (dados.prazo_aditado_dias && contrato?.data_fim) {
          const dataFimAtual = new Date(contrato.data_fim);
          dataFimAtual.setDate(dataFimAtual.getDate() + dados.prazo_aditado_dias);
          contratoAtualizado.data_fim = dataFimAtual.toISOString().split('T')[0];
        }
        
        // Atualizar valor total se houver valor aditado
        if (dados.valor_aditado && contrato?.valor_total) {
          contratoAtualizado.valor_total = contrato.valor_total + dados.valor_aditado;
        }
        
        // Se houver alterações, atualizar o contrato
        if (Object.keys(contratoAtualizado).length > 0) {
          await entities.Contrato.update(dados.contrato_id, contratoAtualizado);
        }
      }
      
      return aditivo;
    },
    onSuccess: () => {
      toast.success("Aditivo criado com sucesso!");
      navigate(createPageUrl(`VisualizarContrato?id=${formData.contrato_id}`));
    },
    onError: (error) => {
      toast.error(getDisplayMessage(error, "Erro ao criar aditivo. Tente novamente."));
    },
  });

  const handleChange = (campo, valor) => {
    setFormData((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10485760) {
      toast.error("Arquivo muito grande! Máximo 10MB.");
      return;
    }

    setUploading(true);
    try {
      const { file_url } = await uploadFile(file);
      setArquivo({ url: file_url, nome: file.name });
      toast.success("Arquivo enviado!");
    } catch (error) {
      toast.error(getDisplayMessage(error, "Erro ao enviar arquivo. Tente novamente."));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.contrato_id || !formData.numero_aditivo || !formData.data_aditivo) {
      toast.error("Preencha todos os campos obrigatórios!");
      return;
    }

    const dadosAditivo = {
      ...formData,
      valor_aditado: parseFloat(formData.valor_aditado) || 0,
      prazo_aditado_dias: parseInt(formData.prazo_aditado_dias) || 0,
    };

    if (arquivo) {
      dadosAditivo.arquivo_aditivo_url = arquivo.url;
      dadosAditivo.arquivo_aditivo_nome = arquivo.nome;
    }

    criarAditivoMutation.mutate(dadosAditivo);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate(createPageUrl(`VisualizarContrato?id=${contratoId}`))}
            className="mb-4 -ml-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            Novo Aditivo Contratual
          </h1>
          <p className="text-slate-600 mt-1">
            Registre um novo termo aditivo ao contrato
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Dados do Aditivo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Identificação */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contrato *</Label>
                  <Select
                    value={formData.contrato_id}
                    onValueChange={(value) => handleChange("contrato_id", value)}
                    disabled={!!contratoId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o contrato" />
                    </SelectTrigger>
                    <SelectContent>
                      {contratos.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.numero_contrato} - {c.orgao_contratante}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Número do Aditivo *</Label>
                  <Input
                    value={formData.numero_aditivo}
                    onChange={(e) => handleChange("numero_aditivo", e.target.value)}
                    placeholder="Ex: 1º Termo Aditivo"
                    required
                  />
                </div>

                <div>
                  <Label>Tipo de Aditivo *</Label>
                  <Select
                    value={formData.tipo_aditivo}
                    onValueChange={(value) => handleChange("tipo_aditivo", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Prazo">Prazo</SelectItem>
                      <SelectItem value="Valor">Valor</SelectItem>
                      <SelectItem value="Escopo">Escopo</SelectItem>
                      <SelectItem value="Misto">Misto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Data do Aditivo *</Label>
                  <Input
                    type="date"
                    value={formData.data_aditivo}
                    onChange={(e) => handleChange("data_aditivo", e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Objeto */}
              <div>
                <Label>Objeto/Justificativa *</Label>
                <Textarea
                  value={formData.objeto}
                  onChange={(e) => handleChange("objeto", e.target.value)}
                  rows={4}
                  placeholder="Descreva o objeto e justificativa do aditivo"
                  required
                />
              </div>

              {/* Valores e Prazos */}
              {(formData.tipo_aditivo === "Valor" || formData.tipo_aditivo === "Misto") && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Valor Aditado (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.valor_aditado}
                      onChange={(e) => handleChange("valor_aditado", e.target.value)}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Use valor negativo para redução
                    </p>
                  </div>
                </div>
              )}

              {(formData.tipo_aditivo === "Prazo" || formData.tipo_aditivo === "Misto") && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Prazo Aditado (dias)</Label>
                    <Input
                      type="number"
                      value={formData.prazo_aditado_dias}
                      onChange={(e) => handleChange("prazo_aditado_dias", e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Nova Data de Fim</Label>
                    <Input
                      type="date"
                      value={formData.nova_data_fim}
                      onChange={(e) => handleChange("nova_data_fim", e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Upload do Arquivo */}
              <div>
                <Label>Arquivo do Aditivo</Label>
                <div className="mt-2">
                  <Input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="file-upload-aditivo"
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById("file-upload-aditivo")?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {arquivo ? "Alterar Arquivo" : "Selecionar Arquivo"}
                      </>
                    )}
                  </Button>
                  {arquivo && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-900">{arquivo.nome}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange("status", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rascunho">Rascunho</SelectItem>
                    <SelectItem value="Ativo">Ativo</SelectItem>
                    <SelectItem value="Cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Observações */}
              <div>
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes}
                  onChange={(e) => handleChange("observacoes", e.target.value)}
                  rows={3}
                  placeholder="Observações adicionais sobre o aditivo"
                />
              </div>

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(createPageUrl(`VisualizarContrato?id=${contratoId}`))}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={criarAditivoMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {criarAditivoMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar Aditivo
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}